import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Eye, EyeOff, Loader2, KeyRound, Ticket } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { authService } from '@/services/auth.service'
import { apiClient } from '@/services/api'
import PasswordStrengthIndicator, { isPasswordStrong } from '@/components/PasswordStrengthIndicator'
import { API_CONFIG, ENDPOINTS } from '@/config/api.config'
import { toast } from 'sonner'
import { getErrorMessage, getErrorDetails } from '@/utils/error'
import { isFCamaraEmail } from '@/utils/fcamara'
import { useAutofillSync } from '@/hooks'
import { isEmailVerificationPending } from '@/types'

const SignUp = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const accessCode = searchParams.get('code')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [couponCode] = useState(searchParams.get('cupom') || '')
  const [couponCompany, setCouponCompany] = useState<string | null>(null)
  const [couponValid, setCouponValid] = useState<boolean | null>(null)

  useEffect(() => {
    if (!couponCode) return

    apiClient
      .get<{ valid: boolean; companyName?: string }>(
        `${ENDPOINTS.coupons.validate}?code=${encodeURIComponent(couponCode)}`
      )
      .then((result) => {
        setCouponValid(result.valid)
        setCouponCompany(result.companyName ?? null)
      })
      .catch(() => {
        setCouponValid(false)
      })
  }, [couponCode])

  // Contas FCamara se cadastram só pelo Microsoft 365 — o formulário de senha
  // some e aparece só o botão 365 quando o e-mail é de domínio FCamara.
  // Autofill/gerenciador de senha preenche sem disparar onChange; o ref
  // sincroniza o valor real do DOM pra detecção reagir mesmo assim.
  const emailRef = useAutofillSync(email, setEmail)
  const emailIsFCamara = isFCamaraEmail(email)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Segurança: e-mail FCamara não se cadastra por senha (o back também rejeita).
    if (emailIsFCamara) return
    setErrors({})
    setIsLoading(true)

    try {
      const result = await authService.register({
        name,
        email,
        password,
        confirmPassword,
        termsAccepted: acceptTerms,
        ...(couponCode && couponValid ? { couponCode } : {}),
      })

      // Cadastro local: conta criada inativa → tela "confirme seu e-mail".
      if (isEmailVerificationPending(result)) {
        navigate('/check-email', { state: { email: result.email } })
      } else {
        toast.success('Conta criada com sucesso!')
        navigate('/home')
      }
    } catch (error: unknown) {
      console.error('Registration error:', error)

      // Handle validation errors from backend (Zod errors → { details: [{ field, message }] }).
      // O interceptor em services/api.ts:113-121 rejeita com um plain object ApiError, não
      // AxiosError — `getErrorDetails` reconhece ambas as formas.
      const details = getErrorDetails(error)
      if (Array.isArray(details)) {
        const fieldErrors: Record<string, string> = {}
        details.forEach((detail: { field: string; message: string }) => {
          fieldErrors[detail.field] = detail.message
        })
        setErrors(fieldErrors)
        toast.error('Por favor, corrija os erros no formulário')
      } else {
        toast.error(getErrorMessage(error, 'Erro ao criar conta. Tente novamente.'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleOutlookSignUp = () => {
    // Redirect to Microsoft OAuth endpoint. Leva o cupom junto pra que o
    // cadastro via Microsoft também vincule empresa + matricule nos cursos
    // (o backend carrega o código pelo state OAuth até o callback).
    const base = `${API_CONFIG.baseURL}/auth/microsoft`
    window.location.href = couponCode ? `${base}?cupom=${encodeURIComponent(couponCode)}` : base
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent/5 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-sm relative z-10"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-primary tracking-wider">AI SYM</h1>
          <p className="text-3xl font-bold text-primary mt-2">Criar Conta</p>
        </div>

        {accessCode && (
          <div className="flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-lg px-3 py-2 mb-6">
            <KeyRound className="w-4 h-4 text-primary shrink-0" />
            <span className="text-sm text-primary">Código de acesso verificado</span>
          </div>
        )}

        {/* Email — sempre visível; define se o cadastro é por senha ou SSO 365 */}
        <div className="space-y-2 mb-6">
          <Label htmlFor="email" className="text-foreground">
            E-mail
          </Label>
          <Input
            ref={emailRef}
            id="email"
            type="email"
            placeholder="Digite seu e-mail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`h-12 bg-muted/30 border-border focus:border-primary ${errors.email ? 'border-red-500' : ''}`}
          />
          {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
        </div>

        {emailIsFCamara && (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              Conta FCamara: o cadastro é feito pelo Microsoft 365.
            </p>
            {/* Microsoft 365 Button */}
            <Button
              variant="outline"
              className="w-full h-12 border-primary text-foreground hover:bg-primary/10 mb-6 flex items-center justify-center gap-3"
              onClick={handleOutlookSignUp}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5">
                <path
                  fill="#0078D4"
                  d="M24 7.387v10.478c0 .23-.08.424-.238.576a.806.806 0 0 1-.587.234h-8.653v-6.18l1.027.756a.349.349 0 0 0 .192.074.324.324 0 0 0 .192-.074l7.905-5.86a.36.36 0 0 1 .162 0z"
                />
                <path
                  fill="#0078D4"
                  d="m24 5.39-8.816 6.548a.612.612 0 0 1-.36.119.612.612 0 0 1-.36-.119L5.649 5.39a.456.456 0 0 1 .11-.339.387.387 0 0 1 .317-.163h17.099c.23 0 .418.076.563.227.145.15.218.334.218.55a.439.439 0 0 1-.044-.181c0-.044.03-.07.088-.094z"
                />
                <path
                  fill="#0078D4"
                  d="M8.857 17.5H0V7.386c0-.318.122-.59.365-.817.244-.228.537-.341.88-.341h7.612v11.272z"
                />
                <path
                  fill="#0364B8"
                  d="M8.857 6.228v11.648a.82.82 0 0 1-.055.303.73.73 0 0 1-.438.445.786.786 0 0 1-.302.053H0V6.847c0-.34.114-.629.341-.866.227-.236.515-.354.863-.354h6.886c.226 0 .42.08.58.238.16.16.24.353.24.58-.02-.116-.033-.184-.053-.217z"
                />
                <path
                  fill="#0078D4"
                  d="m8.857 6.228.04.22c.01.05.013.08.013.126v11.302a.786.786 0 0 1-.053.302.73.73 0 0 1-.438.446.786.786 0 0 1-.302.053H0V6.228h8.857z"
                />
                <path
                  fill="#28A8EA"
                  d="M8.91 17.5H0V6.228h8.857v.376h.053c-.035.097-.053.189-.053.275v10.07c0 .193.035.367.053.55z"
                />
                <path
                  fill="#0078D4"
                  d="M14.522 12.495v6.18h-5.45a.82.82 0 0 1-.303-.054.73.73 0 0 1-.445-.445.786.786 0 0 1-.054-.302V6.994c0-.107.018-.21.054-.306a.73.73 0 0 1 .445-.446.786.786 0 0 1 .302-.053h5.451v6.306z"
                />
                <path
                  fill="#50D9FF"
                  d="M14.522 6.19v5.9l-.022.405h-5.428a.786.786 0 0 1-.302-.053.73.73 0 0 1-.446-.446.82.82 0 0 1-.054-.302V6.994c0-.107.018-.21.054-.306a.73.73 0 0 1 .446-.446.786.786 0 0 1 .302-.053h5.45z"
                />
              </svg>
              Cadastrar com Outlook 365
            </Button>
          </>
        )}

        {/* Coupon Badge */}
        {couponCode && couponValid === true && (
          <div className="flex items-center gap-2 px-4 py-3 mb-6 rounded-lg bg-primary/10 border border-primary/20">
            <Ticket className="w-4 h-4 text-primary shrink-0" />
            <span className="text-sm text-primary font-medium">Cupom {couponCode} aplicado</span>
          </div>
        )}
        {couponCode && couponValid === false && (
          <div className="flex items-center gap-2 px-4 py-3 mb-6 rounded-lg bg-destructive/10 border border-destructive/20">
            <Ticket className="w-4 h-4 text-destructive shrink-0" />
            <span className="text-sm text-destructive font-medium">Cupom inválido</span>
          </div>
        )}

        {/* Form — cadastro por senha (domínios externos) */}
        {!emailIsFCamara && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-foreground">
                Nome Completo
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="Digite seu nome completo"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`h-12 bg-muted/30 border-border focus:border-primary ${errors.name ? 'border-red-500' : ''}`}
              />
              {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">
                Senha
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Crie uma senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`h-12 bg-muted/30 border-border focus:border-primary pr-12 ${errors.password ? 'border-red-500' : ''}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && <p className="text-sm text-red-500">{errors.password}</p>}
              <PasswordStrengthIndicator password={password} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-foreground">
                Confirmar Senha
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirme sua senha"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`h-12 bg-muted/30 border-border focus:border-primary pr-12 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-red-500">{errors.confirmPassword}</p>
              )}
              {password && confirmPassword && password !== confirmPassword && (
                <p className="text-sm text-destructive">As senhas não coincidem</p>
              )}
            </div>

            <div className="flex items-start gap-3 pt-2">
              <Checkbox
                id="terms"
                checked={acceptTerms}
                onCheckedChange={(checked) => setAcceptTerms(checked as boolean)}
                className="mt-0.5"
              />
              <Label
                htmlFor="terms"
                className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
              >
                Concordo com os{' '}
                <span className="text-primary hover:underline">Termos de Serviço</span> e a{' '}
                <span className="text-primary hover:underline">Política de Privacidade</span>
              </Label>
            </div>
            {errors.termsAccepted && (
              <p className="text-sm text-red-500 -mt-2">{errors.termsAccepted}</p>
            )}

            <Button
              type="submit"
              className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium mt-6"
              disabled={
                !acceptTerms ||
                isLoading ||
                !isPasswordStrong(password) ||
                password !== confirmPassword
              }
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Criando conta...
                </>
              ) : (
                'Criar Conta'
              )}
            </Button>
          </form>
        )}

        {/* Link to Sign In */}
        <p className="text-center text-muted-foreground mt-6">
          Já tem uma conta?{' '}
          <Link to="/auth" className="text-primary hover:underline font-medium">
            Entrar
          </Link>
        </p>
      </motion.div>
    </div>
  )
}

export default SignUp
